import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Button, message, Modal } from 'antd';
import WorkOrderTable from '../WorkOrderTable';
import NoAddWorkOrderHeader from '../WorkOrderTable/TableHeader/noAddWorkOrderHeader';
import OWButton from '../ow_button/OWButton';
import { EmptyState } from '../empty_state/EmptyState';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { linkWalkthroughTaskWorkOrder } from '../../thunks/work_orders_thunks';

const MODAL_TC = 'walkthroughTaskLinkWorkOrdersModal';
const MESSAGE_KEY = 'walkthrough-task-wo-link';

/** The fields this picker actually reads off a work order row. */
interface LinkableWorkOrder {
	id: number;
	/** Set on a work order already attached to some walkthrough task (hydrated entity). */
	walkThroughId?: number;
	/** The search-index counterpart of the above — indexed, but `walkThroughId` is not. */
	siteSurveyTaskTitleId?: string;
}

/** The task answer being linked to — `record` in the expanded walkthrough row. */
interface LinkableTaskAnswer {
	/** MD5 the backend derives per task answer during hydration; not a stored column. */
	titleId?: string;
	workOrders?: LinkableWorkOrder[];
}

/** The walkthrough the task belongs to. */
interface LinkableWalkthrough {
	id?: number;
	locationId?: number;
	buyerCompanyId?: number;
}

interface WalkthroughTaskLinkWorkOrderButtonProps {
	walkthrough: LinkableWalkthrough;
	taskAnswer: LinkableTaskAnswer;
	userType?: string;
	/** Resolves with the re-hydrated walkthrough; rejects with a message string. */
	linkWorkOrder?: (
		workOrderId: React.Key,
		walkthroughId: number,
		siteSurveyTaskTitleId: string
	) => Promise<unknown>;
}

const WalkthroughTaskLinkWorkOrderButton: FC<WalkthroughTaskLinkWorkOrderButtonProps> = ({
	walkthrough,
	taskAnswer,
	userType,
	linkWorkOrder,
}) => {
	const [modalVisible, setModalVisible] = useState(false);
	const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
	const [loading, setLoading] = useState(false);

	const walkthroughId = nullSafeGet('id', walkthrough);
	const siteSurveyTaskTitleId = nullSafeGet('titleId', taskAnswer);

	const linkedIds = useMemo(
		() =>
			nullSafeGetOrElse('workOrders', taskAnswer, []).map(
				(workOrder) => `${nullSafeGet('id', workOrder)}`
			),
		[taskAnswer]
	);

	const openModal = useCallback(() => setModalVisible(true), []);
	const closeModal = useCallback(() => {
		setModalVisible(false);
		setSelectedRowKeys([]);
	}, []);

	const rowSelection = useMemo(
		() => ({
			selectedRowKeys,
			onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
			// A work order carries a single walkthrough task (walkthroughId/siteSurveyTaskTitleId are
			// scalar columns), so one already spoken for can't be picked — the backend rejects it too.
			// Table rows come from the search index, which indexes siteSurveyTaskTitleId but carries
			// no walkthroughId; walkThroughId is checked as well for the hydrated-entity case.
			getCheckboxProps: (record: LinkableWorkOrder) => ({
				disabled:
					linkedIds.includes(`${record.id}`) ||
					!!nullSafeGet('siteSurveyTaskTitleId', record) ||
					!!nullSafeGet('walkThroughId', record),
			}),
		}),
		[selectedRowKeys, linkedIds]
	);

	// Default the picker to the walkthrough's location, the way the work-order-to-work-order
	// link modal seeds from the current work order's location. Seeded only when the walkthrough
	// actually carries one — `locationIds: [undefined]` filters the table down to nothing, which
	// reads as "this location has no work orders" rather than "we don't know the location".
	const initialFilters = useMemo(() => {
		const locationId = nullSafeGet('locationId', walkthrough);
		return locationId ? { locationIds: [locationId] } : {};
	}, [walkthrough]);

	const preAppliedFilters = useMemo(() => {
		const filters: { buyerCompanyId?: number } = {};
		if (userType === ROLE_TYPES.SUPPLIER) {
			filters.buyerCompanyId = nullSafeGet('buyerCompanyId', walkthrough);
		}
		return filters;
	}, [userType, walkthrough]);

	const handleLink = useCallback(
		(ids: React.Key[]) => {
			setLoading(true);
			message.loading({ content: 'Linking work orders…', key: MESSAGE_KEY });
			// Sequential so each request sees the previous link applied — the task answer's
			// workOrderIds is a read-modify-write on the server.
			const linkedSoFar: React.Key[] = [];
			ids
				.reduce(
					(chain, id) =>
						chain.then(() =>
							linkWorkOrder(id, walkthroughId, siteSurveyTaskTitleId).then(() => {
								linkedSoFar.push(id);
							})
						),
					Promise.resolve()
				)
				.then(() => {
					message.success({
						content: ids.length > 1 ? 'Work orders linked' : 'Work order linked',
						key: MESSAGE_KEY,
						duration: 1.5,
					});
					// No refetch needed — each link responds with the re-hydrated walkthrough and the
					// thunk pushes it into the walkthroughs slice the detail page renders from.
					closeModal();
				})
				.catch((err) => {
					// Partway through the chain the modal stays open so the user can retry, but every
					// link that already landed has disabled its own row (it is in linkedIds now) while
					// staying checked — an un-clearable selection that re-sends completed work on retry.
					// Drop them, so what remains selected is exactly what still needs linking.
					setSelectedRowKeys((keys) => keys.filter((key) => !linkedSoFar.includes(key)));
					message.error({
						content: linkedSoFar.length
							? `Linked ${linkedSoFar.length} of ${ids.length} work orders. ${
									err || 'Failed to link the rest'
							  }`
							: err || 'Failed to link work orders',
						key: MESSAGE_KEY,
					});
				})
				.finally(() => setLoading(false));
		},
		[linkWorkOrder, walkthroughId, siteSurveyTaskTitleId, closeModal]
	);

	const linkSelected = useCallback(
		() => handleLink(selectedRowKeys),
		[handleLink, selectedRowKeys]
	);

	return (
		<>
			<Button size="middle" onClick={openModal} style={{ marginRight: 8 }}>
				Link Work Order
			</Button>
			{modalVisible ? (
				<Modal
					width="80%"
					visible
					title="Link Work Orders"
					closable
					onCancel={closeModal}
					footer={
						<>
							<Button onClick={closeModal}>Cancel</Button>
							<OWButton
								type="primary"
								loading={loading}
								disabled={!selectedRowKeys.length}
								onClick={linkSelected}
							>
								Link{selectedRowKeys.length ? ` (${selectedRowKeys.length})` : ''}
							</OWButton>
						</>
					}
					wrapClassName="linked-work-orders-modal"
				>
					{linkedIds.length ? (
						<div className="mb-4">
							<Alert
								message={`${linkedIds.length} Work Order(s) linked to this task`}
								description={linkedIds.map((id) => `WO #${id}`).join(', ')}
								type="info"
							/>
						</div>
					) : null}
					<WorkOrderTable
						key={MODAL_TC}
						targetCollectionName={MODAL_TC}
						preAppliedFilters={preAppliedFilters}
						initialFilters={initialFilters}
						renderEmptyState={() => <EmptyState headline="No Work Orders" />}
						rowSelection={rowSelection}
						simplifyRows
						CustomHeader={NoAddWorkOrderHeader}
					/>
				</Modal>
			) : null}
		</>
	);
};

const mapDispatchToProps = (dispatch, ownProps) => ({
	linkWorkOrder: (workOrderId, walkthroughId, siteSurveyTaskTitleId) =>
		dispatch(
			linkWalkthroughTaskWorkOrder(ownProps.userType)(
				workOrderId,
				walkthroughId,
				siteSurveyTaskTitleId
			)
		),
});

export default withUserType(
	connect(null, mapDispatchToProps)(WalkthroughTaskLinkWorkOrderButton as any) as any
);
